import { Link } from "react-router-dom";
import { FileText, CheckCircle, Clock, TrendingUp, ArrowLeft, ArrowRight, MessageSquare } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useClientDashboard } from "@/hooks/api/use-dashboard";
import { useTenders } from "@/hooks/api/use-tenders";
import { useAuth } from "@/contexts/AuthContext";
import { useAccountStatus } from "@/contexts/AccountStatusContext";
import ReadOnlyWrapper from "@/components/ReadOnlyWrapper";

const ClientDashboard = () => {
  const { t, isRTL } = useLanguage();
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
  const { user } = useAuth();
  const { isActive, accountStatus } = useAccountStatus();
  
  // Client-specific data
  const { data: clientDashboardData, isLoading: clientDashboardLoading } = useClientDashboard({ enabled: !!user });
  const { data: clientTendersData } = useTenders({ page: 1, per_page: 3 }, { enabled: !!user });

  const stats = [
    {
      title: t('dashboard.totalOffers') || 'إجمالي العروض',
      value: clientDashboardData?.data?.total_offers?.toString() || "0",
      icon: FileText,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      title: t('dashboard.pendingOffers') || 'العروض المعلقة',
      value: clientDashboardData?.data?.pending_offers?.toString() || "0",
      icon: Clock,
      color: "text-amber-600",
      bgColor: "bg-amber-100",
    },
    {
      title: t('dashboard.acceptedOffers') || 'العروض المقبولة',
      value: clientDashboardData?.data?.accepted_offers?.toString() || "0",
      icon: CheckCircle,
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      title: t('dashboard.rejectedOffers') || 'العروض المرفوضة',
      value: clientDashboardData?.data?.rejected_offers?.toString() || "0",
      icon: TrendingUp,
      color: "text-red-600",
      bgColor: "bg-red-100",
    },
    {
      title: t('dashboard.totalProjects') || 'إجمالي المشاريع',
      value: clientDashboardData?.data?.total_projects?.toString() || "0",
      icon: FileText,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
  ];

  const recentTenders = Array.isArray(clientTendersData?.data) ? clientTendersData.data : [];

  if (clientDashboardLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-4 rounded" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-12" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 border rounded">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-6 w-16 rounded" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Message - Different content based on status */}
      <div className={`p-6 rounded-lg ${
        isActive 
          ? 'bg-gradient-to-r from-primary to-primary/80 text-white' 
          : accountStatus?.account_status === 'pending'
          ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
          : accountStatus?.account_status === 'suspended'
          ? 'bg-orange-50 border border-orange-200 text-orange-800'
          : 'bg-red-50 border border-red-200 text-red-800'
      }`}>
        <h1 className="text-2xl font-bold mb-2">
          {isActive 
            ? `${t('dashboard.welcome')}, ${user?.name}!`
            : accountStatus?.account_status === 'pending'
            ? t('accountStatus.pending.welcome') || 'Account Under Review'
            : accountStatus?.account_status === 'suspended'
            ? t('accountStatus.suspended.welcome') || 'Account Suspended'
            : t('accountStatus.rejected.welcome') || 'Account Rejected'
          }
        </h1>
        <p className={isActive ? 'text-primary-100' : 'opacity-90'}>
          {isActive 
            ? t('dashboard.welcomeMessage')
            : accountStatus?.account_status === 'pending'
            ? t('accountStatus.pending.welcomeMessage') || 'Your account is currently under review. You can view your data but cannot make changes until approval.'
            : accountStatus?.account_status === 'suspended'
            ? t('accountStatus.suspended.welcomeMessage') || 'Your account has been temporarily suspended. You can view your data but cannot make changes.'
            : t('accountStatus.rejected.welcomeMessage') || 'Your account has been permanently rejected. You can only view and export your data.'
          }
        </p>
      </div>

      {/* Stats Cards - Hide for non-active accounts */}
      {isActive && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-2 rounded ${stat.bgColor}`}>
                    <Icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Recent Tenders */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('dashboard.recentTenders')}</CardTitle>
          <ReadOnlyWrapper>
            <Link to="/dashboard/tenders">
              <Button variant="outline" size="sm">
                {t('dashboard.viewAll')}
                <ArrowIcon className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </ReadOnlyWrapper>
        </CardHeader>
        <CardContent>
          {recentTenders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('dashboard.noRecentTenders')}
            </div>
          ) : (
            <div className="space-y-4">
              {recentTenders.map((tender: any) => (
                <div key={tender.id} className="flex items-center justify-between p-4 border rounded">
                  <div className="space-y-1">
                    <h4 className="font-medium">{tender.title}</h4>
                    <p className="text-sm text-muted-foreground">
                      {new Date(tender.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Badge 
                      variant={tender.status === 'active' ? 'default' : 'secondary'}
                      className="text-white"
                    >
                      {t(`common.${tender.status}`) || tender.status}
                    </Badge>
                    <ReadOnlyWrapper>
                      <Link to={`/dashboard/tenders/${tender.id}`}>
                        <Button variant="outline" size="sm">
                          {t('common.view')}
                        </Button>
                      </Link>
                    </ReadOnlyWrapper>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions - Different for active vs non-active */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isActive ? (
          // Active account actions
          <>
            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <Link to="/dashboard/tenders" className="block p-6">
                <div className="flex items-center space-x-3">
                  <FileText className="h-8 w-8 text-primary" />
                  <div>
                    <h3 className="font-semibold">{t('dashboard.browseTenders')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard.findNewTenders')}
                    </p>
                  </div>
                </div>
              </Link>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <Link to="/dashboard/applications" className="block p-6">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-8 w-8 text-primary" />
                  <div>
                    <h3 className="font-semibold">{t('dashboard.myApplications')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard.trackApplications')}
                    </p>
                  </div>
                </div>
              </Link>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <Link to="/dashboard/portfolio" className="block p-6">
                <div className="flex items-center space-x-3">
                  <TrendingUp className="h-8 w-8 text-primary" />
                  <div>
                    <h3 className="font-semibold">{t('dashboard.myPortfolio')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard.managePortfolio')}
                    </p>
                  </div>
                </div>
              </Link>
            </Card>
          </>
        ) : (
          // Non-active account actions
          <>
            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <Link to="/dashboard/tenders" className="block p-6">
                <div className="flex items-center space-x-3">
                  <FileText className="h-8 w-8 text-primary" />
                  <div>
                    <h3 className="font-semibold">{t('dashboard.viewTenders')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard.browseAvailableTenders')}
                    </p>
                  </div>
                </div>
              </Link>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <Link to="/dashboard/applications" className="block p-6">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-8 w-8 text-primary" />
                  <div>
                    <h3 className="font-semibold">{t('dashboard.viewApplications')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard.viewApplicationHistory')}
                    </p>
                  </div>
                </div>
              </Link>
            </Card>

            {(accountStatus?.account_status === 'suspended' || accountStatus?.account_status === 'rejected') && (
              <Card className="cursor-pointer hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-center space-x-3">
                    <MessageSquare className="h-8 w-8 text-primary" />
                    <div>
                      <h3 className="font-semibold">{t('dashboard.contactSupport')}</h3>
                      <p className="text-sm text-muted-foreground">
                        {t('dashboard.getHelpWithAccount')}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientDashboard;
